/* eslint-disable no-nested-ternary, no-underscore-dangle */
import React, { useEffect, useState } from 'react';

import {
  Badge, Button, ButtonGroup, Dropdown, Form, OverlayTrigger, Table,
  Tooltip,
} from 'react-bootstrap';
import OperatorInputModal from './OperatorInputModal';
import OrderInputModal from './OrderInputModal';
import OrderDetailsModal from './OrderDetailsModal';
import TripConfirmationModal from './TripConfirmationModal';
import LinkedBookingSelectorModal from './LinkedBookingSelectorModal';
import { formatBookingDateOnly, formatBookingTimeOnly } from '../../../Utils/timezone';
import OrderWarnings from './OrderWarnings';
import SortByColumn from './SortByColumn';
import DriverInputModal from './DriverInputModal';
import thousandSeparator from '../../../Utils/numberFormat';
import profitDisplay from '../Functions/profitDisplay';
import UserService from '../../../Services/UserService';
import Permissions from '../../../Utils/Permissions';
import PickupGuideInputModal from './PickupGuideInputModal';
import AddInvoiceModal from './AddInvoiceModal';
import useDocumentIssuer from '../../../Components/Billingo/useDocumentIssuer';
import Error from '../../../Components/Error';
import isInvoicingEnabled from '../../../Utils/partnerInvoicing';
import TripClaimsRowBadge from '../../../Components/TripClaims/TripClaimsRowBadge';
import PassengerMessageButton from '../../../Components/PassengerMessage/PassengerMessageButton';
import PassengerMessageModal from './PassengerMessageModal';
import TripClaimsRefusalAlert from '../../../Components/TripClaims/TripClaimsRefusalAlert';
import { TRIP_CLAIMS_CHANGED, tripClaimsOf } from '../../../Utils/tripClaims';
import { BOOKING_TYPE_LABELS, operatorRoleBadge } from '../../../config/bookingConstants';
import { isStepAvailable } from '../../../Utils/bookingType';
import { formatOperatorPriceTotals } from '../../../Utils/operatorCost';
import {
  isSystemManagedStatus, systemManagedStatusLabel, bookingStatusBadgeClass,
} from '../../../Utils/bookingStatus';

/**
 * Clone an order with only the fields visible in OrderInputModal
 * Explicitly define which fields to include to prevent copying
 * system-generated fields (_id, booking_reference) or
 * operator-related fields (operator_id, driver_id, etc.)
 */
function cloneOrderForInput(order) {
  return {
    // Partner Information
    partner_id: order.partner_id,
    partner_booking_reference: order.partner_booking_reference,

    // Booking Type
    booking_type: order.booking_type || 'transfer',

    // Passenger Information
    full_name: order.full_name,
    email_address: order.email_address,
    phone_number: order.phone_number,

    // Travel Information
    date_datetime: order.date_datetime,
    timezone: order.timezone,
    destination_from: order.destination_from,
    destination_to: order.destination_to,

    // Programme
    programme: order.programme,

    // Flight and Comments
    flight_number: order.flight_number,
    comments: order.comments,
    admin_comments: order.admin_comments,

    // Passengers
    passengers: order.passengers,

    // Order Details (default to 'Confirmed' status)
    status: 'Confirmed',
    category: order.category,

    // Payment Information
    original_price: order.original_price,
    price: order.price,
    currency: order.currency,
    payment: {
      type: order.payment?.type,
      paid: false,
    },

    // Discount
    discount: order.discount,

    // Billing Information
    billing: order.billing,

    // Event Booking Fields
    event_name: order.event_name,
    event_group: order.event_group,
    is_event_booking: order.is_event_booking,
    passenger_manifest: order.passenger_manifest,

    // Experience/Programme Details
    important_information: order.important_information,
    cancellation_policy: order.cancellation_policy,
  };
}

function OrdersTable({
  columns = {
    warnings: false,
    booking_type: false,
    partner: true,
    driver: false,
    partner_booking_reference: false,
    customer_name: true,
    operator: true,
    operator_price: false,
    email_address: false,
    transfer_date: true,
    from: true,
    pax: true,
    to: true,
    price: true,
    status: true,
    category: false,
    actions: {
      orders: true,
    },
  },
  orders,
  setFilters,
  markAsCompleted,
  revertToConfirmed,
  setPage,
  page,
  totalPages,
  archiveOrder,
  isLoading,
  fetchOrders,
  orderInputModal,
  operatorInputModal,
  orderDetailsModal,
  driverInputModal,
  tripConfirmationModal,
  pickupGuideModal,
  addInvoiceModal,
  sortFn,
  // Open a booking on a given Order Details tab: `{ order, tab }`.
  detailsRequest = null,
}) {
  // State for linked booking selector modal
  const [linkedSelectorOrder, setLinkedSelectorOrder] = useState(null);
  // The Billingo document flow (quick "create document"), opened with the
  // order's existing documents loaded.
  const documentIssuer = useDocumentIssuer({ onIssued: fetchOrders });
  // Track which tab to open in OrderDetailsModal
  const [detailsInitialTab, setDetailsInitialTab] = useState('overview');
  // Which mobile cards are expanded, keyed by booking_reference (collapsed by default).
  const [expandedCards, setExpandedCards] = useState({});
  // The booking whose passenger is being written to and on which channel, or
  // null. ONE composer for the whole table — a page of thirty rows must not
  // mount thirty modals — and one composer for both channels, driven by which
  // trigger was tapped rather than by a second modal.
  const [messaging, setMessaging] = useState(null);

  const canSeeProfit = UserService.getInstance().canAccessModule(Permissions.CAN_SEE_GLOBAL_STATS);

  const openReminders = (order) => {
    setDetailsInitialTab('reminders');
    orderDetailsModal.show(order);
  };

  /**
   * Turning a page is a deliberate move to a new page, so it starts at the
   * top. The fetch used to do this for every reload of the list, which also
   * yanked the reader to the top when they had just come Back and
   * ScrollMemory had put them where they left off; the page turn is the one
   * moment that genuinely means it.
   */
  const goToPage = (nextPage) => {
    setPage(nextPage);
    window.scrollTo(0, 0);
  };

  // The booking's Driver events tab: its trip claims and terminal log.
  const openDriverEvents = (order) => {
    setDetailsInitialTab('driver-events');
    orderDetailsModal.show(order);
  };

  // The booking's Order Ledger tab: its order lines and adjustments.
  const openLedger = (order) => {
    setDetailsInitialTab('ledger');
    orderDetailsModal.show(order);
  };

  // A request to open a booking on a given tab (e.g. /orders?booking=…&tab=…).
  useEffect(() => {
    if (!detailsRequest?.order) return;
    setDetailsInitialTab(detailsRequest.tab || 'overview');
    orderDetailsModal.show(detailsRequest.order);
  }, [detailsRequest]);

  // After any review, re-read the list so the rows' "to review" badges follow.
  useEffect(() => {
    const refresh = () => fetchOrders?.();
    window.addEventListener(TRIP_CLAIMS_CHANGED, refresh);
    return () => window.removeEventListener(TRIP_CLAIMS_CHANGED, refresh);
  }, [fetchOrders]);

  // Handle voucher generation - show selector if linked bookings exist
  const handleGenerateVoucher = (order) => {
    if (order.linked_booking_ids?.length > 0) {
      setLinkedSelectorOrder(order);
    } else {
      tripConfirmationModal.show(order);
    }
  };

  // Handle booking selection from linked booking selector
  const handleBookingSelected = (selectedBooking) => {
    tripConfirmationModal.show(selectedBooking);
  };

  // The per-row action menu, shared by the desktop table cell and the mobile
  // collapsible cards so there is a single source of truth for row actions.
  const renderRowActions = (order, isLocked) => (
    <Dropdown as={ButtonGroup}>
      <ButtonGroup>
        <Button
          variant="primary"
          onClick={() => orderDetailsModal.show(order)}
        >
          Details
        </Button>
        <Button
          variant="outline-primary"
          aria-label="Order Line Adjustments"
          onClick={() => openLedger(order)}
        >
          <i className="fa fa-euro-sign" />
        </Button>
      </ButtonGroup>

      <Dropdown.Toggle variant="outline-primary" split id="dropdown-split-basic" />

      <Dropdown.Menu>
        <Dropdown.Item
          disabled={isLocked || order.status === 'Completed'}
          onClick={() => {
            orderInputModal.show(order);
          }}
        >
          Edit
        </Dropdown.Item>
        {order.linked_booking_ids?.length > 0 && (
        <Dropdown.Item
          // eslint-disable-next-line no-underscore-dangle
          onClick={() => setFilters({ linked_booking_ids: order._id })}
        >
          <i className="fa fa-link me-1" />
          Show Linked Bookings (
          {order.linked_booking_ids.length}
          )
        </Dropdown.Item>
        )}
        <Dropdown.Item
          onClick={() => {
            orderInputModal.show(cloneOrderForInput(order));
          }}
        >
          Clone
        </Dropdown.Item>
        {/* Invoicing-enabled partner orders manage invoices from
          the Details modal (Create / Upload), so the standalone
          upload is hidden for them to avoid two entry points.
          BOTH doors close on an expense: they are two entry points to the
          same step, and invoicing a cost record to a customer is not a
          thing. The cost itself is recorded in the Order Ledger, which
          stays. */}
        {isStepAvailable(order, 'add-invoice')
          && !(order.partner_id && isInvoicingEnabled(order.partner)) && (
          <Dropdown.Item
            disabled={isLocked || order.status === 'Completed' || order.status === 'Request'}
            onClick={() => {
              addInvoiceModal.show(order);
            }}
          >
            Add Invoice
          </Dropdown.Item>
        )}
        {isStepAvailable(order, 'add-invoice')
          && order.partner_id && isInvoicingEnabled(order.partner) && (
          <Dropdown.Item
            onClick={() => documentIssuer.issue({ group_key: order._id, booking_ids: [order._id] })}
            disabled={documentIssuer.preparing === order._id}
          >
            <i className="fa fa-file-invoice-dollar me-1" />
            Create financial document
          </Dropdown.Item>
        )}
        <Dropdown.Item
          disabled={isLocked || !order.operator_id || order.status === 'Completed'}
          onClick={() => {
            markAsCompleted(order);
          }}
        >
          Mark Completed
          {' '}
          {order.email_address && '& Send Review Email' }
        </Dropdown.Item>
        {order.status === 'Completed' && (
        <Dropdown.Item
          onClick={() => {
            revertToConfirmed(order);
          }}
        >
          <i className="fa fa-undo me-1" />
          Revert to Confirmed
        </Dropdown.Item>
        )}
        {/* Hide Pickup Guide for experiences — and for expenses, which have
            no pickup to guide anybody to. */}
        {order.booking_type !== 'experience' && isStepAvailable(order, 'pickup-guide') && (
        <Dropdown.Item
          disabled={isLocked || !order.partner?.driver_portal_enabled}
          onClick={() => {
            pickupGuideModal.show(order);
          }}
        >
          Assign Pickup Guide
        </Dropdown.Item>
        )}
        <Dropdown.Item
          disabled={isLocked || order.status === 'Completed'}
          onClick={() => {
            operatorInputModal.show(order);
          }}
        >
          Assign Operator
        </Dropdown.Item>
        {/* Hide Assign Driver & Car for experiences — and for expenses:
            dispatching a car to a fuel receipt is the exact confusion this
            booking type exists to end. */}
        {order.booking_type !== 'experience' && isStepAvailable(order, 'assign-driver') && (
        <Dropdown.Item
          disabled={isLocked || order.status !== 'Confirmed' || !order.operator_id}
          onClick={() => {
            driverInputModal.show(order);
          }}
        >
          Assign Driver & Car
        </Dropdown.Item>
        )}
        {/* Hide Driver Events for experiences — unless claims wait on one.
            An expense is hidden unconditionally: no driver was ever on it,
            so there is nothing that could have raised a claim. */}
        {isStepAvailable(order, 'driver-events')
          && (order.booking_type !== 'experience' || tripClaimsOf(order).total > 0) && (
          <Dropdown.Item
            disabled={!order.operator_id && !tripClaimsOf(order).total}
            onClick={() => openDriverEvents(order)}
          >
            Driver Events
            {tripClaimsOf(order).total > 0 && ` · ${tripClaimsOf(order).total}`}
          </Dropdown.Item>
        )}
        {/* A voucher and a trip confirmation are both documents handed to
            a PASSENGER about a journey. */}
        {isStepAvailable(order, 'generate-voucher') && (
        <Dropdown.Item
          onClick={() => handleGenerateVoucher(order)}
        >
          {order.booking_type === 'experience' ? 'Generate Voucher' : 'Generate Trip Confirmation'}
        </Dropdown.Item>
        )}
        <Dropdown.Item
          onClick={() => {
            archiveOrder(order);
          }}
        >
          Archive
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );

  // Mobile-only collapsible card for a single order. Header (always visible)
  // shows passenger, time/date and status; the rest expands on tap.
  const renderMobileCard = (order) => {
    const isLocked = isSystemManagedStatus(order.status);
    const isOpen = !!expandedCards[order.booking_reference];
    const typeInfo = BOOKING_TYPE_LABELS[order.booking_type] || BOOKING_TYPE_LABELS.transfer;
    const statusVariant = bookingStatusBadgeClass(order.status);
    const priceText = `${thousandSeparator(order.total_price) ?? thousandSeparator(order.price)} ${order.currency || 'EUR'}`;
    // Profit indicator — the same helper the desktop Price column uses.
    const profit = canSeeProfit && (order.status === 'Confirmed' || order.status === 'Completed')
      ? profitDisplay(order.profit_summary)
      : null;
    return (
      <div className="order-mcard" key={order.booking_reference}>
        <button
          type="button"
          className="order-mcard-header"
          onClick={() => setExpandedCards((prev) => ({
            ...prev,
            [order.booking_reference]: !prev[order.booking_reference],
          }))}
          aria-expanded={isOpen}
        >
          <div className="order-mcard-head-main">
            <span className="order-mcard-name">{order.full_name || '—'}</span>
            <span className="order-mcard-when">
              <i className="fa fa-clock me-1" />
              {order.date_datetime ? formatBookingTimeOnly(order, 'date_datetime') : '—'}
              {order.date_datetime && (
                <span className="text-muted ms-1">
                  {formatBookingDateOnly(order, 'date_datetime')}
                </span>
              )}
            </span>
          </div>
          <div className="order-mcard-head-side">
            <div className="order-mcard-head-meta">
              <span className={`badge ${statusVariant}`}>{order.status}</span>
              <span className="order-mcard-price">{priceText}</span>
              {profit && (
                <span className={`order-mcard-margin text-${profit.tone}`} title={profit.title}>{profit.text}</span>
              )}
            </div>
            <i className={`fa fa-chevron-${isOpen ? 'up' : 'down'} order-mcard-chevron`} />
          </div>
        </button>
        {tripClaimsOf(order).total > 0 && (
          <div className="px-3 pb-2">
            <TripClaimsRowBadge order={order} onOpen={openDriverEvents} />
          </div>
        )}

        {isOpen && (
          <div className="order-mcard-body">
            <div className="order-mcard-field">
              <span className="order-mcard-label">From</span>
              <span>
                <i className="fa fa-map-marker-alt me-1 text-success" />
                {order.destination_from?.address || '—'}
              </span>
            </div>
            <div className="order-mcard-field">
              <span className="order-mcard-label">To</span>
              <span>
                <i className="fa fa-map-marker-alt me-1 text-danger" />
                {order.destination_to?.address || '—'}
              </span>
            </div>
            <div className="order-mcard-field">
              <span className="order-mcard-label">Operator(s)</span>
              <span>
                {order.operators?.length > 0
                  ? order.operators.map((op) => op.operator?.name || 'Unassigned').join(', ')
                  : 'Unassigned'}
              </span>
            </div>
            <div className="order-mcard-field">
              <span className="order-mcard-label">Pax</span>
              <span>{order.passengers?.amount || 0}</span>
            </div>
            <div className="order-mcard-field">
              <span className="order-mcard-label">Partner</span>
              <span>{order.partner?.name || '—'}</span>
            </div>
            <div className="order-mcard-field">
              <span className="order-mcard-label">Type</span>
              <span>
                <Badge bg={typeInfo.color} className="text-nowrap">
                  <i className={`fa ${typeInfo.icon} me-1`} />
                  {typeInfo.label}
                </Badge>
              </span>
            </div>
            {order.flight_number && (
              <div className="order-mcard-field">
                <span className="order-mcard-label">Flight</span>
                <span>{order.flight_number}</span>
              </div>
            )}
            <div className="order-mcard-actions">
              {renderRowActions(order, isLocked)}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <TripClaimsRefusalAlert />
      <Error error={documentIssuer.error} setError={documentIssuer.setError} />
      {/* Desktop: the full table. Mobile uses the collapsible card list below. */}
      <div className="d-none d-md-block">
        <Table responsive striped bordered hover className="mt-2">
          <thead>
            <tr>
              {columns.warnings && <th />}
              {columns.customer_name && <th>Passenger</th>}
              {columns.transfer_date && (
              <th style={{ whiteSpace: 'nowrap' }}>
                Transfer date
                <SortByColumn
                  column="date_datetime"
                  sortFn={sortFn}
                />
              </th>
              )}
              {columns.booking_type && <th>Type</th>}
              {columns.partner && <th>Partner</th>}
              {columns.partner_booking_reference && <th>Partner #</th>}
              {columns.operator && <th>Operator(s)</th>}
              {columns.operator_price && <th>Operator Price</th>}
              {columns.driver && <th>Driver</th>}
              {columns.pax && <th>Pax</th>}
              {columns.flight_number && <th>Flight number</th>}
              {columns.email_address && <th>Email address</th>}
              {columns.from && <th>From</th>}
              {columns.to && <th>To</th>}
              {columns.price && <th>Price</th>}
              {columns.status && <th>Status</th>}
              <th />
            </tr>
          </thead>
          {!isLoading && (
          <tbody>
            {orders.map((order) => {
            // System-managed statuses (Pending Ingestion, Ingestion Failed)
            // are owned by the partner ingestion pipeline. The operator must
            // not edit, dispatch, or otherwise act on these rows until the
            // backend transitions them out of the locked state.
              const isLocked = isSystemManagedStatus(order.status);
              return (
                <tr
                  key={order.booking_reference}
                  className={[
                    order.booking_type === 'experience' ? 'experience-row' : '',
                    isLocked ? 'table-secondary' : '',
                  ].filter(Boolean).join(' ') || undefined}
                  title={isLocked ? systemManagedStatusLabel(order.status) : undefined}
                >
                  {columns.warnings && (
                  <td data-label="Alerts">
                    <OrderWarnings
                      assignOperator={() => operatorInputModal.show(order)}
                      assignDriver={() => driverInputModal.show(order)}
                      markAsCompleted={markAsCompleted}
                      assignPickupGuide={() => pickupGuideModal.show(order)}
                      openReminders={openReminders}
                      order={order}
                    />
                  </td>
                  )}
                  {columns.customer_name && (
                  <td data-label="Passenger">
                    <div className="fw-bold">
                      {order.full_name || '-'}
                      {order.linked_booking_ids?.length > 0 && (
                      <OverlayTrigger
                        placement="top"
                        overlay={(
                          <Tooltip>
                            {order.linked_booking_ids.length}
                            {' '}
                            linked booking(s) - click to search
                          </Tooltip>
                      )}
                      >
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0 ms-1"
                        // eslint-disable-next-line no-underscore-dangle
                          onClick={() => setFilters({ linked_booking_ids: order._id })}
                        >
                          <i className="fa fa-link text-primary" />
                        </Button>
                      </OverlayTrigger>
                      )}
                    </div>
                    {order.phone_number && (
                    <small className="text-muted d-block">
                      <i className="fa fa-phone me-1" />
                      {order.phone_number}
                      {/* The passenger's number is the affordance for
                          writing to them. The DRIVER cell further along this
                          row is the same markup to the character and gets no
                          such control — the gate is the data path, inside
                          the component. */}
                      <PassengerMessageButton
                        order={order}
                        onOpen={(booking, channel) => setMessaging({ order: booking, channel })}
                        className="ms-2 align-baseline"
                      />
                    </small>
                    )}
                    <TripClaimsRowBadge order={order} onOpen={openDriverEvents} className="mt-1" />
                  </td>
                  )}
                  {columns.transfer_date && (
                  <td data-label="Date">
                    {order.return_date && <span className="badge text-bg-primary mb-1">Return Journey</span>}
                    {order.date_datetime ? (
                      <>
                        <div className="fw-bold text-primary">
                          <i className="fa fa-clock me-1" />
                          {formatBookingTimeOnly(order, 'date_datetime')}
                        </div>
                        <small className="text-muted">
                          {formatBookingDateOnly(order, 'date_datetime')}
                        </small>
                      </>
                    ) : <span className="text-danger">INCORRECT_DATE</span>}
                    {order.return_date_datetime && (
                    <div className="mt-1 pt-1 border-top">
                      <div className="fw-bold text-primary">
                        <i className="fa fa-clock me-1" />
                        {formatBookingTimeOnly(order, 'return_date_datetime')}
                      </div>
                      <small className="text-muted">
                        <i className="fa fa-right-left me-1" />
                        {formatBookingDateOnly(order, 'return_date_datetime')}
                      </small>
                    </div>
                    )}
                  </td>
                  )}
                  {columns.booking_type && (
                  <td data-label="Type">
                    {(() => {
                      const typeInfo = BOOKING_TYPE_LABELS[order.booking_type]
                    || BOOKING_TYPE_LABELS.transfer;
                      return (
                        <Badge bg={typeInfo.color} className="text-nowrap">
                          <i className={`fa ${typeInfo.icon} me-1`} />
                          {typeInfo.label}
                        </Badge>
                      );
                    })()}
                  </td>
                  )}
                  {columns.partner && (
                  <td data-label="Partner">
                    <div className="d-flex align-items-center gap-1 flex-wrap">
                      {order.partner?.name}
                      {order.visitor_tracking?.data?.gclid && (
                      <Badge bg="info" text="dark" style={{ fontSize: '0.65em' }}>
                        Google Ads
                      </Badge>
                      )}
                    </div>
                    {order.partner_booking_reference && (
                    <small className="text-muted">
                      {order.partner_booking_reference}
                    </small>
                    )}
                  </td>
                  )}
                  {columns.partner_booking_reference && <td data-label="Partner #">{order.partner_booking_reference}</td>}
                  {columns.operator && (
                  <td data-label="Operator(s)">
                    {/* For bookings with multiple operators */}
                    {order.operators?.length > 0 ? (
                      <div className="d-flex flex-column gap-1">
                        {order.operators.map((op) => {
                          // By TYPE first: a cost bucket on a trip is not
                          // a crew member in a minor role.
                          const roleInfo = operatorRoleBadge(op.role, op.operator);
                          const opKey = `${op.role}-${op.operator_id || 'unassigned'}`;
                          return (
                            <div key={opKey} className="d-flex align-items-center gap-1">
                              <Badge bg={roleInfo.color} className="text-nowrap" style={{ fontSize: '0.7em' }}>
                                <i className={`fa ${roleInfo.icon}`} />
                              </Badge>
                              <span className="small">{op.operator?.name || 'Unassigned'}</span>
                              {op.operator_id && !op.update_acknowledged && (
                              <OverlayTrigger
                                placement="top"
                                overlay={(
                                  <Tooltip>
                                    {roleInfo.label}
                                    {' '}
                                    has not acknowledged
                                  </Tooltip>
                              )}
                              >
                                <span className="fa fa-warning text-danger" style={{ fontSize: '0.8em' }} />
                              </OverlayTrigger>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                    /* Legacy single operator display */
                      order.operator?.name ? (
                        <div>
                          <div className="fw-bold">
                            <i className="fa fa-building me-1 text-muted" />
                            {order.operator.name}
                            {!order.operator_update_acknowledged && (
                            <OverlayTrigger
                              placement="top"
                              overlay={(
                                <Tooltip>Operator has not acknowledged</Tooltip>
                          )}
                            >
                              <span className="fa fa-warning text-danger ms-1" />
                            </OverlayTrigger>
                            )}
                          </div>
                        </div>
                      ) : (
                        <span className="text-warning">
                          <i className="fa fa-exclamation-triangle me-1" />
                          Unassigned
                        </span>
                      )
                    )}
                    {/* Category badge for transfers */}
                    {order.booking_type !== 'experience' && order.category?.name && (
                      (() => {
                        const isPremium = ['business', 'premium', 'luxury', 'lux'].some(
                          (kw) => order.category.name.toLowerCase().includes(kw),
                        );
                        return isPremium ? (
                          <Badge bg="warning" text="dark" className="mt-1" style={{ fontSize: '0.7em' }}>
                            <i className="fa fa-star me-1" />
                            {order.category.name}
                          </Badge>
                        ) : (
                          <Badge bg="light" text="dark" className="mt-1 border" style={{ fontSize: '0.7em' }}>
                            <i className="fa fa-car me-1" />
                            {order.category.name}
                          </Badge>
                        );
                      })()
                    )}
                  </td>
                  )}
                  {columns.operator_price && (
                  <td data-label="Operator Price">
                    {order.operators?.length > 0 ? (
                    /* Multi-operator: the same resolved cost the Order Details
                       operator panel shows, grouped per currency. This column
                       used to sum `op.price` directly, which is the entry layer
                       only — so the 66 production bookings whose cost sits in
                       the ledger line or the legacy mirror rendered "0" here
                       while really owing money (2026-11-01). Shares one rule
                       with the panel via Utils/operatorCost; do not re-derive
                       it here. '—' rather than '0' when nothing resolves: no
                       cost found is not a cost of zero. */
                      formatOperatorPriceTotals(order.operators) || '—'
                    ) : (
                    // Legacy single operator
                      <>
                        {thousandSeparator(order.operator_price)}
                        {' '}
                        {order.operator_currency}
                      </>
                    )}
                  </td>
                  )}
                  {columns.driver && (
                  <td data-label="Driver">
                    {order.driver ? (
                      <div>
                        <div className="fw-bold">
                          <i className="fa fa-user me-1 text-muted" />
                          {order.driver.full_name}
                        </div>
                        {order.driver.phone_number && (
                        <small className="text-muted d-block">
                          <i className="fa fa-phone me-1" />
                          {order.driver.phone_number}
                        </small>
                        )}
                        {order.car && (
                        <small className="text-muted d-block">
                          <i className="fa fa-car me-1" />
                          {order.car.type}
                          {order.car.color && ` (${order.car.color})`}
                          {order.car.plate_number && ` - ${order.car.plate_number}`}
                        </small>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  )}
                  {columns.pax && (
                  <td data-label="Pax">
                    <div className="fw-bold">{order.passengers?.amount || 0}</div>
                    {order.passengers?.adults > 0 && (
                    <small className="text-muted d-block">
                      {order.passengers.adults}
                      {' '}
                      adult
                      {order.passengers.adults > 1 ? 's' : ''}
                    </small>
                    )}
                    {order.booking_type === 'experience' ? (
                      order.passengers?.children > 0 && (
                      <small className="text-muted d-block">
                        {order.passengers.children}
                        {' '}
                        child
                        {order.passengers.children > 1 ? 'ren' : ''}
                      </small>
                      )
                    ) : (
                      (order.passengers?.convertible_seat > 0
                    || order.passengers?.booster_seat > 0
                    || order.passengers?.infant_carrier > 0) && (
                    <small className="text-muted d-block">
                      {(order.passengers?.convertible_seat || 0)
                        + (order.passengers?.booster_seat || 0)
                        + (order.passengers?.infant_carrier || 0)}
                      {' '}
                      child
                      {((order.passengers?.convertible_seat || 0)
                        + (order.passengers?.booster_seat || 0)
                        + (order.passengers?.infant_carrier || 0)) > 1 ? 'ren' : ''}
                    </small>
                      )
                    )}
                  </td>
                  )}
                  {columns.flight_number && <td data-label="Flight number">{order.flight_number}</td>}
                  {columns.email_address && (
                  <td data-label="Email address">
                    <Button
                      variant="link"
                      className="p-0 align-baseline text-decoration-none link-primary"
                      onClick={() => setFilters({ email_address: order.email_address })}
                    >
                      {order.email_address}
                    </Button>
                    {/* The mirror of the phone cell's control, on the
                        address. Same component, same two gates — the one
                        difference is which field has to be there. */}
                    <PassengerMessageButton
                      order={order}
                      channel="email"
                      onOpen={(booking, channel) => setMessaging({ order: booking, channel })}
                      className="ms-2 align-baseline"
                    />
                  </td>
                  )}
                  {columns.from && (
                  <td data-label="From">
                    <small>
                      <i className="fa fa-map-marker-alt me-1 text-success" />
                      {order.destination_from.address}
                    </small>
                    {order.programme?.name && (
                    <div
                      className="text-muted mt-1"
                      style={{
                        maxWidth: '250px',
                        fontSize: '0.875em',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        wordBreak: 'break-word',
                      }}
                    >
                      <i className="fa-solid fa-cube me-1" />
                      {order.programme?.name}
                      {' '}
                      (
                      {order.programme?.total_hours}
                      h)
                    </div>
                    )}
                  </td>
                  )}
                  {columns.to && (
                  <td data-label="To">
                    <small>
                      <i className="fa fa-map-marker-alt me-1 text-danger" />
                      {order.destination_to.address}
                    </small>
                  </td>
                  )}
                  {columns.price && (
                  <td data-label="Price">
                    {thousandSeparator(order.total_price) ?? thousandSeparator(order.price)}
                    {' '}
                    {order.currency || 'EUR'}
                    {canSeeProfit
                    && (order.status === 'Confirmed' || order.status === 'Completed')
                    && (() => {
                      const profit = profitDisplay(order.profit_summary);
                      if (!profit) return null;
                      return (
                        <div
                          className={`text-${profit.tone} fw-medium`}
                          style={{ fontSize: '0.75em', lineHeight: 1.1, marginTop: '2px' }}
                          title={profit.title}
                        >
                          {profit.text}
                        </div>
                      );
                    })()}
                  </td>
                  )}
                  {columns.status && (
                  <td data-label="Status">
                    <div className="d-flex align-items-center gap-1">
                      <span className={`badge ${bookingStatusBadgeClass(order.status)}`}>
                        {order.status}
                      </span>
                      {
                  order.notifications?.some((n) => !n.is_completed
                    && new Date(n.due_date) <= new Date()) && (
                    <Button
                      size="sm"
                      variant="warning"
                      className="p-0 px-1"
                      title="Reminder overdue"
                      onClick={() => openReminders(order)}
                    >
                      <i className="fa fa-bell" />
                    </Button>
                  )
}
                    </div>
                    {/* Booking type badge below status */}
                    <div className="mt-1">
                      {(() => {
                        const typeInfo = BOOKING_TYPE_LABELS[order.booking_type]
                      || BOOKING_TYPE_LABELS.transfer;
                        return (
                          <Badge
                            bg="light"
                            text="dark"
                            className="border"
                            style={{ fontSize: '0.7em' }}
                          >
                            <i className={`fa ${typeInfo.icon} me-1`} />
                            {typeInfo.label}
                          </Badge>
                        );
                      })()}
                    </div>
                  </td>
                  )}
                  {columns.actions.orders && (
                  <td>
                    {renderRowActions(order, isLocked)}
                  </td>
                  )}
                </tr>
              );
            })}
          </tbody>
          ) }
          { isLoading && (
          <tbody>
            {[...Array(3)].map((_) => (
              <tr key={_}>
                {Object.entries(columns).map(([key, value]) => (value === true ? (
                  <td key={key} className="placeholder-glow">
                    <span className="w-100 placeholder" />
                  </td>
                ) : null))}
                <td className="placeholder-glow">
                  <span className="w-100 placeholder" />
                </td>
              </tr>
            ))}
          </tbody>
          ) }
        </Table>
      </div>

      {/* Mobile: collapsible cards (collapsed by default) */}
      <div className="d-md-none order-mcards mt-2">
        {!isLoading && orders.length === 0 && (
          <div className="text-center text-muted py-4">No bookings found.</div>
        )}
        {!isLoading && orders.map(renderMobileCard)}
        {isLoading && (
          <div className="text-center text-muted py-4">
            <i className="fa fa-spin fa-spinner me-2" />
            Loading…
          </div>
        )}
      </div>

      <div className="d-block mt-2">
        <ButtonGroup>
          <Button
            onClick={() => goToPage(Math.max(page - 1, 1))}
            disabled={page === 1}
            variant="secondary"
            size="sm"
          >
            Previous
          </Button>
          <Button
            onClick={() => goToPage(page + 1)}
            disabled={page === totalPages}
            variant="secondary"
            size="sm"
          >
            Next
          </Button>
        </ButtonGroup>

        {/* Select field for direct page navigation */}
        <Form.Select
          aria-label="Select page"
          onChange={(e) => goToPage(Number(e.target.value))}
          value={page}
          size="sm"
          style={{ width: 'auto', display: 'inline-block' }}
          className="ms-2"
        >
          {Array.from({ length: totalPages }, (key, i) => (
            <option key={i + 1} value={i + 1}>
              Page
              {' '}
              {i + 1}
            </option>
          ))}
        </Form.Select>

        <p>
          Page
          {' '}
          {page}
          {' '}
          of
          {' '}
          {totalPages}
        </p>
      </div>

      { isLoading && <div>Loading...</div> }

      {orderDetailsModal.isVisible && (
      <OrderDetailsModal
        order={orderDetailsModal.selectedObject}
        show={orderDetailsModal.isVisible}
        handleClose={() => { setDetailsInitialTab('overview'); orderDetailsModal.hide(); }}
        initialTab={detailsInitialTab}
      />
      ) }

      {/* The table's door onto the composer. No `autoDraft`: somebody who
          tapped a passenger's phone number has a line in mind, and the
          automatic draft belongs to the abandoned-basket panel in
          OrderDetailsModal and nowhere else. */}
      {messaging && (
      <PassengerMessageModal
        show={Boolean(messaging)}
        channel={messaging.channel}
        booking={messaging.order}
        onHide={() => setMessaging(null)}
      />
      ) }

      {orderInputModal.isVisible && (
      <OrderInputModal
        originalOrder={orderInputModal.selectedObject}
        show={orderInputModal.isVisible}
        fetchOrders={fetchOrders}
        handleClose={orderInputModal.hide}
      />
      ) }

      { operatorInputModal.isVisible && (
      <OperatorInputModal
        originalOrder={operatorInputModal.selectedObject}
        show={operatorInputModal.isVisible}
        handleClose={operatorInputModal.hide}
        fetchOrders={fetchOrders}
      />
      ) }

      { driverInputModal.isVisible && (
      <DriverInputModal
        originalOrder={driverInputModal.selectedObject}
        show={driverInputModal.isVisible}
        handleClose={driverInputModal.hide}
        fetchOrders={fetchOrders}
      />
      ) }

      { tripConfirmationModal.isVisible && (
      <TripConfirmationModal
        order={tripConfirmationModal.selectedObject}
        show={tripConfirmationModal.isVisible}
        handleClose={tripConfirmationModal.hide}
      />
      ) }

      { pickupGuideModal.isVisible && (
      <PickupGuideInputModal
        originalOrder={pickupGuideModal.selectedObject}
        show={pickupGuideModal.isVisible}
        handleClose={pickupGuideModal.hide}
        fetchOrders={fetchOrders}
      />
      ) }

      { addInvoiceModal.isVisible && (
      <AddInvoiceModal
        originalOrder={addInvoiceModal.selectedObject}
        show={addInvoiceModal.isVisible}
        handleClose={addInvoiceModal.hide}
        fetchOrders={fetchOrders}
      />
      ) }

      {documentIssuer.modal}

      { linkedSelectorOrder && (
      <LinkedBookingSelectorModal
        order={linkedSelectorOrder}
        show={!!linkedSelectorOrder}
        handleClose={() => setLinkedSelectorOrder(null)}
        onSelectBooking={handleBookingSelected}
      />
      ) }
    </>
  );
}

export default OrdersTable;
